import React from 'react';
import type { ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { formatAmountParts, formatLongDate, formatMoney } from '../i18n/format';

export interface VoucherCardProps {
  theme: ShopTheme;
  /** Face value of the voucher. */
  amount: number;
  currency: string;
  currencySymbol: string;
  venueName: string;
  venueAddress?: string;
  /** Left blank on the shop preview, where no code exists yet. */
  code?: string;
  expiryDate?: string | Date;
  message?: string;
  /** Printed under the "Gift voucher" label, e.g. "00 4821". */
  serial?: string;
  /** Remaining balance, shown only once part of the voucher has been spent. */
  remainingAmount?: number;
  className?: string;
}

/**
 * The voucher itself — one card design used on the shop, the confirmation, the voucher
 * page and (as an HTML table) the email. Value and venue on the left, code on a
 * perforated stub on the right; on a phone the perforation turns horizontal and the stub
 * sits underneath, so the code stays legible across a bar rather than being shrunk.
 */
const VoucherCard: React.FC<VoucherCardProps> = ({
  theme,
  amount,
  currency,
  currencySymbol,
  venueName,
  venueAddress,
  code,
  expiryDate,
  message,
  serial,
  remainingAmount,
  className = '',
}) => {
  const { t, locale } = useI18n();
  const { whole, separator, cents } = formatAmountParts(amount, locale);
  const expiry = formatLongDate(expiryDate, locale);
  const partlySpent = typeof remainingAmount === 'number' && remainingAmount < amount;

  const label: React.CSSProperties = {
    font: `600 10px/1 ${theme.fontFamily}`,
    letterSpacing: '.22em',
    textTransform: 'uppercase',
    color: theme.label,
  };

  return (
    <div
      className={`w-full overflow-hidden ${className}`}
      style={{
        background: theme.surface,
        border: `1px solid ${theme.hairline}`,
        borderRadius: theme.radius,
        borderTop: `3px solid ${theme.accent}`,
        boxShadow: '0 1px 1px rgba(28,26,23,.04)',
        fontFamily: theme.fontFamily,
      }}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Value side */}
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-5 p-6 sm:gap-7 sm:p-[30px] sm:pt-[34px]">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            {theme.logoUrl ? (
              <img
                src={theme.logoUrl}
                alt={venueName}
                className="block h-auto w-auto object-contain"
                style={{ maxWidth: Math.min(theme.logoWidth, 160), maxHeight: Math.min(theme.logoHeight, 44) }}
              />
            ) : (
              <span
                style={{
                  font: `500 15px/1.2 ${theme.fontFamily}`,
                  letterSpacing: '.01em',
                  color: theme.ink,
                }}
              >
                {venueName}
              </span>
            )}
            <div className="min-w-0 text-right">
              <div
                style={{
                  font: `600 9px/1.3 ${theme.fontFamily}`,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: theme.label,
                }}
              >
                {t('card.gift_voucher')}
              </div>
              {serial && (
                <div
                  className="mt-[3px]"
                  style={{ font: `500 10px/1.3 ${theme.monoFamily}`, letterSpacing: '.04em', color: theme.faint }}
                >
                  {t('card.number', { serial })}
                </div>
              )}
            </div>
          </div>

          <div>
            <div style={label}>{t('card.value')}</div>
            <div className="mt-2 flex items-baseline gap-2" style={{ color: theme.ink }}>
              <span style={{ font: `400 clamp(22px, 5vw, 30px)/1 ${theme.fontFamily}`, color: theme.body }}>
                {currencySymbol}
              </span>
              <span
                style={{
                  font: `500 clamp(48px, 11vw, 68px)/.9 ${theme.fontFamily}`,
                  letterSpacing: '-.03em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {whole}
              </span>
              {cents && (
                <span
                  style={{
                    font: `500 clamp(20px, 4vw, 26px)/1 ${theme.fontFamily}`,
                    fontVariantNumeric: 'tabular-nums',
                    color: theme.body,
                  }}
                >
                  {separator}
                  {cents}
                </span>
              )}
            </div>

            {partlySpent && (
              <div className="mt-2" style={{ font: `400 13px/1.4 ${theme.fontFamily}`, color: theme.muted }}>
                {t('card.remaining', { amount: formatMoney(remainingAmount!, currency, locale) })}
              </div>
            )}

            <div className="mt-3.5 h-px" style={{ background: theme.hairline }} />
            {/* With a logo in the corner the venue is named once, here; without one the
                name is already set above, so this line carries the address alone. */}
            {(theme.logoUrl || venueAddress) && (
              <div className="mt-3.5" style={{ font: `400 14px/1.5 ${theme.fontFamily}`, color: theme.body }}>
                {theme.logoUrl && <span style={{ fontWeight: 600, color: theme.ink }}>{venueName}</span>}
                {venueAddress && (
                  <span style={{ color: theme.label }}>
                    {theme.logoUrl ? ' · ' : ''}
                    {venueAddress}
                  </span>
                )}
              </div>
            )}

            {message && (
              <div
                className="mt-3 pl-3"
                style={{
                  borderLeft: `2px solid ${theme.hairlineSoft}`,
                  font: `400 13px/1.55 ${theme.fontFamily}`,
                  color: theme.muted,
                }}
              >
                {message}
              </div>
            )}
          </div>
        </div>

        {/* Perforation — vertical beside the stub, horizontal above it on a phone */}
        <div
          className="hidden w-px sm:block"
          style={{
            background: `repeating-linear-gradient(to bottom, ${theme.perforation} 0 5px, transparent 5px 10px)`,
          }}
        />
        <div
          className="h-px sm:hidden"
          style={{
            background: `repeating-linear-gradient(to right, ${theme.perforation} 0 5px, transparent 5px 10px)`,
          }}
        />

        {/* Code stub */}
        <div
          className="flex flex-col justify-between gap-5 p-5 sm:w-[236px] sm:min-w-[172px] sm:shrink-0 sm:gap-6 sm:px-[22px] sm:pb-[26px] sm:pt-[34px]"
          style={{ background: theme.stub }}
        >
          <div>
            <div style={label}>{t('card.voucher_code')}</div>
            <div
              className="mt-3 px-2.5 py-3 text-center"
              style={{
                background: theme.surface,
                border: `1px solid ${theme.hairline}`,
                borderRadius: Math.min(theme.radius, 2),
                font: `500 clamp(17px, 4.4vw, 21px)/1 ${theme.monoFamily}`,
                letterSpacing: '.16em',
                overflowWrap: 'anywhere',
                color: code ? theme.ink : theme.faint,
              }}
            >
              {code || t('card.code_placeholder')}
            </div>
            <div className="mt-3" style={{ font: `400 12px/1.5 ${theme.fontFamily}`, color: theme.label }}>
              {t('card.quote_code')}
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="h-px" style={{ background: theme.hairline }} />
            {expiry && (
              <div>
                <div style={label}>{t('card.valid_until')}</div>
                <div className="mt-1.5" style={{ font: `500 14px/1.2 ${theme.fontFamily}`, color: theme.ink }}>
                  {expiry}
                </div>
              </div>
            )}
            <div style={{ font: `400 11px/1.45 ${theme.fontFamily}`, color: theme.faint }}>{t('card.terms')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherCard;
